/**
 * MIT License
 *
 * Copyright (c) 2023–Present PPResume (https://ppresume.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

:root {
  --border-color: #d0d0d0;

  /* text colors */
  --text-color-primary: #000000;
  --text-color-secondary: #333333;

  /* spacing */
  --spacing-xss: 2px;
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 12px;
  --spacing-lg: 16px;
  --spacing-xl: 24px;
  --spacing-xxl: 40px;
  --spacing-xxxl: 80px;

  /* font sizes */
  --font-size-xs: 0.8rem;
  --font-size-sm: 0.9rem;
  --font-size-md: 1rem;
  --font-size-lg: 1.1rem;
  --font-size-xl: 1.25rem;
  --font-size-xxl: 2.5rem;

  /* layout */
  --page-max-width: 900px;

  /* typography */
  --text-default-font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu,
    Cantarell, sans-serif;
}

html {
  font-size: var(--text-font-size);
}

body {
  /* font & text */
  color: var(--text-color-primary);
  font-family: var(--text-font-family);
  /* this variable is defined by `typography.fontSize` in resume html layout */
  text-autospace: normal;

  /* layout */
  line-height: var(--line-height);
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding: var(--spacing-xxxl) var(--spacing-xxl) var(--spacing-xxl);
}

/* header */
.resume-header {
  margin-bottom: var(--spacing-xxl);
  text-align: center;
}

.resume-name {
  font-size: var(--font-size-xxl);
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
}

.resume-headline {
  font-size: var(--font-size-lg);
  margin-bottom: var(--spacing-md);
}

/* contact info */
.resume-contact-info {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: var(--spacing-lg);
  row-gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
}

.resume-contact-icon,
.resume-contact-label {
  margin-right: var(--spacing-sm);
  vertical-align: middle;
}

/* links: modern typographic style */
a {
  color: var(--text-color-primary);
  text-decoration: underline;
  text-decoration-color: var(--border-color);
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  text-decoration-skip-ink: auto;
  transition: text-decoration-color 0.2s;
}

a:hover {
  text-decoration-color: var(--text-color-primary);
}

/* sections */
.resume-section {
  margin-bottom: var(--spacing-xl);
}

.resume-section-title {
  font-size: var(--font-size-xl);
  font-weight: 500;
  margin-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: var(--spacing-xs);
}

/* entries */
.resume-entry {
  margin-bottom: var(--spacing-lg);
}

.resume-entry:last-child {
  margin-bottom: 0;
}

.resume-entry-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xs);
}

.resume-entry-title {
  font-weight: 500;
  margin-bottom: var(--spacing-xss);
}

.resume-entry-summary p,
.resume-summary-content p {
  margin-bottom: var(--spacing-xs);
}

/* lists */
.resume-entry-summary ul,
.resume-entry-summary ol,
.resume-summary-content ul,
.resume-summary-content ol {
  padding-left: var(--spacing-lg);
  list-style-type: circle;
}

/* labeled lists (courses, keywords, etc.) */
.resume-labeled-list span {
  font-weight: 500;
}

/* skills grid */
.resume-skill-item,
.resume-language-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: var(--spacing-xs);
}

.resume-skill-name,
.resume-language-name {
  font-weight: 500;
}

.resume-skill-level,
.resume-language-fluency {
  font-weight: 400;
}

/* mobile */
@media (max-width: 768px) {
  body {
    padding: var(--spacing-xl);
  }

  .resume-header {
    margin-bottom: var(--spacing-xl);
  }

  .resume-contact-info {
    flex-direction: column;
    align-items: center;
  }

  .resume-entry-header {
    flex-wrap: wrap;
  }

  .resume-skill-item,
  .resume-language-item {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* footer */
.resume-footer {
  margin-top: var(--spacing-xxl);
  padding-top: var(--spacing-xl);
  border-top: 1px solid var(--border-color);
  font-size: var(--font-size-sm);
  color: var(--text-color-secondary);
}

@media print {
  .resume-footer {
    display: none;
  }
}
